import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaUserPlus, FaUsers, FaMicrochip, FaSignOutAlt } from "react-icons/fa";

import { CircuitBackgroundLayer } from "../../components/CircuitBackground/AnimatedCircuitChipBackground";
import styles from "../Monitor/Monitor.module.css";

const AdminDashboard = ({ onLogout }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  })();

  return (
    <div className={styles.container}>
      <CircuitBackgroundLayer />
      <div className={styles.overlay}></div>

      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.deviceTitle}>
            <span style={{ fontSize: isMobile ? "1.75rem" : "2rem" }}>🛠️</span>
            <div>
              <div style={{ fontWeight: "bold" }}>داشبورد مدیریت</div>
              <span style={{ fontSize: "12px", color: "#ccc" }}>
                {user?.name
                  ? `${user.name} ${user.family || ""}`
                  : "اپراتور مدیریت"}{" "}
                | نقش: admin
              </span>
            </div>
          </div>

          <div style={getHeaderActionStyle(isMobile)}>
            <button
              onClick={onLogout}
              className={styles.logoutBtn}
              type="button"
            >
              <FaSignOutAlt /> خروج
            </button>
          </div>
        </div>

        <div style={getAdminGridStyle(isMobile)}>
          <AdminCard
            icon={<FaUserPlus size={isMobile ? 30 : 36} color="#2ecc71" />}
            title="تعریف کاربر و دستگاه"
            description="ایجاد مشتری جدید، تولید رمز اولیه و اتصال دستگاه به کاربر"
            onClick={() => navigate("/admin/create-user-device")}
            isMobile={isMobile}
          />

          <AdminCard
            icon={<FaUsers size={isMobile ? 30 : 36} color="#3498db" />}
            title="مدیریت کاربران"
            description="لیست کاربران، وضعیت، ریست رمز و مشاهده دستگاه‌ها"
            onClick={() => navigate("/admin/users")}
            isMobile={isMobile}
          />

          <AdminCard
            icon={<FaMicrochip size={isMobile ? 30 : 36} color="#f1c40f" />}
            title="مدیریت دستگاه‌ها"
            description="لیست دستگاه‌ها، مالک، تاریخ شروع و پایان سرویس"
            onClick={() => navigate("/admin/devices")}
            isMobile={isMobile}
          />
        </div>
      </div>
    </div>
  );
};

const AdminCard = ({
  icon,
  title,
  description,
  badge,
  onClick,
  isMobile = false,
}) => {
  return (
    <div style={getAdminCardStyle(isMobile)} onClick={onClick}>
      {badge && <div style={badgeStyle}>{badge}</div>}

      <div style={getIconBoxStyle(isMobile)}>{icon}</div>

      <h3 style={getCardTitleStyle(isMobile)}>{title}</h3>
      <p style={getCardDescStyle(isMobile)}>{description}</p>
    </div>
  );
};

const useIsMobile = (breakpoint = 720) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false,
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
};

const adminGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: "20px",
  width: "100%",
  marginTop: "30px",
  direction: "rtl",
};

const adminCardStyle = {
  position: "relative",
  background: "rgba(31, 45, 58, 0.92)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "18px",
  padding: "24px",
  minHeight: "190px",
  color: "#fff",
  cursor: "pointer",
  boxShadow: "0 10px 35px rgba(0,0,0,0.25)",
  transition: "transform 0.2s ease, border-color 0.2s ease",
  overflow: "hidden",
};

const iconBoxStyle = {
  width: "62px",
  height: "62px",
  borderRadius: "16px",
  background: "rgba(255,255,255,0.06)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "18px",
};

const cardTitleStyle = {
  margin: "0 0 10px",
  fontSize: "18px",
  color: "#f8fafc",
};

const cardDescStyle = {
  margin: 0,
  fontSize: "13px",
  lineHeight: "1.9",
  color: "#bdc3c7",
};

const badgeStyle = {
  position: "absolute",
  top: "14px",
  left: "14px",
  background: "rgba(241, 196, 15, 0.16)",
  border: "1px solid rgba(241, 196, 15, 0.35)",
  color: "#f1c40f",
  fontSize: "11px",
  borderRadius: "999px",
  padding: "4px 10px",
};

const getHeaderActionStyle = (isMobile) => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  ...(isMobile
    ? {
        width: "100%",
        justifyContent: "flex-end",
      }
    : {}),
});

const getAdminGridStyle = (isMobile) => ({
  ...adminGridStyle,
  ...(isMobile
    ? {
        gridTemplateColumns: "1fr",
        gap: "14px",
        marginTop: "18px",
      }
    : {}),
});

const getAdminCardStyle = (isMobile) => ({
  ...adminCardStyle,
  ...(isMobile
    ? {
        padding: "18px",
        minHeight: "142px",
        borderRadius: "16px",
      }
    : {}),
});

const getIconBoxStyle = (isMobile) => ({
  ...iconBoxStyle,
  ...(isMobile
    ? {
        width: "52px",
        height: "52px",
        borderRadius: "14px",
        marginBottom: "14px",
      }
    : {}),
});

const getCardTitleStyle = (isMobile) => ({
  ...cardTitleStyle,
  ...(isMobile ? { fontSize: "16px" } : {}),
});

const getCardDescStyle = (isMobile) => ({
  ...cardDescStyle,
  ...(isMobile ? { fontSize: "12px", lineHeight: "1.8" } : {}),
});

export default AdminDashboard;
